/**
 * Which song the Pitch home and the song pickers offer first (owner goal 2026-10-20). A song is
 * worth a pitch from before its release until 14 days after it, and an unreleased song with no date
 * yet. One rule for the API (`outreach.home`) and the web pickers. Pure.
 */

/** The days after the release day in which a song still gets the hero of the Pitch home. */
export const OUTREACH_PITCH_AFTER_RELEASE_DAYS = 14;

const DAY_MS = 24 * 60 * 60 * 1000;

export interface PitchSongCandidate {
  /** ISO date or Date. Null: no release day yet. */
  releaseDate: string | Date | null;
  /** True for an unreleased catalog row (kind `unreleased`). */
  unreleased: boolean;
}

function timeOf(value: string | Date | null): number | null {
  if (value === null) {
    return null;
  }
  const time = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isFinite(time) ? time : null;
}

/**
 * True when the song may take the hero: a release day from 14 days ago on, or an unreleased song
 * with no day. A released song older than 14 days is not "next" (the pickers still list it).
 */
export function isPitchWindowSong(song: PitchSongCandidate, now: Date): boolean {
  const time = timeOf(song.releaseDate);
  if (time === null) {
    return song.unreleased;
  }
  return time >= now.getTime() - OUTREACH_PITCH_AFTER_RELEASE_DAYS * DAY_MS;
}

/**
 * The order of the songs: first the next upcoming one (nearest date), then the songs released in
 * the last 14 days (newest first), then the unreleased songs with no date, then every other song
 * (newest first). The first song is the one to pitch.
 */
export function comparePitchSongs(a: PitchSongCandidate, b: PitchSongCandidate, now: Date): number {
  const byRank = rankOf(a, now) - rankOf(b, now);
  return byRank !== 0 ? byRank : tieBreak(a, b, now);
}

function rankOf(song: PitchSongCandidate, now: Date): number {
  const time = timeOf(song.releaseDate);
  if (time === null) {
    return song.unreleased ? 2 : 3;
  }
  if (time >= now.getTime() - DAY_MS) {
    return 0;
  }
  return isPitchWindowSong(song, now) ? 1 : 3;
}

function tieBreak(a: PitchSongCandidate, b: PitchSongCandidate, now: Date): number {
  const ta = timeOf(a.releaseDate) ?? 0;
  const tb = timeOf(b.releaseDate) ?? 0;
  // The upcoming songs go nearest first; the released songs newest first.
  return rankOf(a, now) === 0 ? ta - tb : tb - ta;
}

/** The songs in pitch order (a copy). */
export function sortPitchSongs<T extends PitchSongCandidate>(songs: readonly T[], now: Date): T[] {
  return [...songs].sort((a, b) => comparePitchSongs(a, b, now));
}

/** The song to pitch now, or null when no song is in the pitch window. */
export function nextPitchSong<T extends PitchSongCandidate>(songs: readonly T[], now: Date): T | null {
  return sortPitchSongs(songs, now).find((song) => isPitchWindowSong(song, now)) ?? null;
}
